import { useState } from "react";
import { reasonsFor, type FeedbackTarget } from "@notamen/core/feedback";
import { Icon } from "@/components/ui/Icon/Icon";
import { useSubmitFeedback, type SubmitFeedbackRequest } from "@/lib/queries";
import styles from "./FeedbackControls.module.css";

/** The reaction context (everything except target/sentiment/reasons/source). */
type FeedbackContext = Omit<
  SubmitFeedbackRequest,
  "target" | "sentiment" | "reasons" | "source"
>;

/**
 * 👍/👎 + reason controls for a generated caption or image (or a queue reject).
 * A LIKE is one tap (no reason). A DISLIKE opens the reason chips for this
 * target and requires at least one before submitting — mirroring the server
 * contract. Every action is a real POST; the user sees a real confirmation.
 * No undo / edit / count (those weren't asked for).
 */
export function FeedbackControls({
  ws,
  brand,
  target,
  source,
  context,
}: {
  ws: string;
  brand: string;
  target: FeedbackTarget;
  source: "studio" | "queue";
  context: FeedbackContext;
}) {
  const submit = useSubmitFeedback(ws, brand);
  const [mode, setMode] = useState<"idle" | "disliking" | "done">("idle");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<"like" | "dislike" | null>(null);

  const dislikeReasons = reasonsFor(target, "dislike");

  function like() {
    submit.mutate(
      { target, sentiment: "like", reasons: [], source, ...context },
      {
        onSuccess: () => {
          setDone("like");
          setMode("done");
        },
      },
    );
  }

  function toggle(code: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function submitDislike() {
    if (picked.size === 0) return;
    submit.mutate(
      { target, sentiment: "dislike", reasons: [...picked], source, ...context },
      {
        onSuccess: () => {
          setDone("dislike");
          setMode("done");
        },
      },
    );
  }

  if (mode === "done") {
    return (
      <div className={styles.bar}>
        <span className={styles.thanks}>
          <Icon name="check" size={12} />
          {done === "dislike" ? "Thanks — we’ll steer away from that." : "Thanks — noted what works."}
        </span>
      </div>
    );
  }

  if (mode === "disliking") {
    return (
      <div className={styles.bar}>
        <span className={styles.prompt}>What’s off? Pick at least one:</span>
        <div className={styles.chips}>
          {dislikeReasons.map((r) => (
            <button
              key={r.code}
              type="button"
              className={`${styles.chip} ${picked.has(r.code) ? styles.chipOn : ""}`}
              onClick={() => toggle(r.code)}
              aria-pressed={picked.has(r.code)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancel}
            onClick={() => {
              setMode("idle");
              setPicked(new Set());
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.submit}
            onClick={submitDislike}
            disabled={picked.size === 0 || submit.isPending}
          >
            {submit.isPending ? "Saving…" : "Submit"}
          </button>
        </div>
        {submit.isError && <span className={styles.err}>Couldn’t save that — try again.</span>}
      </div>
    );
  }

  return (
    <div className={styles.bar}>
      <span className={styles.label}>Helpful?</span>
      <button
        type="button"
        className={styles.thumb}
        onClick={like}
        disabled={submit.isPending}
        aria-label="Like — this is good"
        title="This is good"
      >
        👍
      </button>
      <button
        type="button"
        className={styles.thumb}
        onClick={() => setMode("disliking")}
        disabled={submit.isPending}
        aria-label="Dislike — tell us what’s off"
        title="Tell us what’s off"
      >
        👎
      </button>
      {submit.isError && <span className={styles.err}>Couldn’t save — try again.</span>}
    </div>
  );
}
